/**
 * iter-170p — CapAwareButton
 *
 * Drop-in wrapper around <Button> that:
 *   1. Hard-disables the button when ``cap.atHardLimit``.
 *   2. Renders a popover with upsell CTA on hover/click when blocked.
 *   3. Shows the inline ``CapUsageBadge`` right next to the button.
 *
 * Props:
 *   resourceKey   — catalog key, e.g. "cash_accounts"
 *   featureLabel  — i18n'd label shown in the popover (e.g. "cuentas")
 *   isEs          — Spanish flag (other languages fall back to English)
 *   ...rest       — passed through to the underlying <Button>
 */
import { useState } from "react";
import { useResourceCap } from "../hooks/useResourceCap";
import { CapUsageBadge } from "./CapUsageBadge";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export function CapAwareButton({
  resourceKey,
  featureLabel,
  isEs,
  children,
  className = "",
  // iter-196g — opt-out for the inline badge below the button (set
  // ``true`` when the page renders the cap badge in its PageHeader so
  // the indicator doesn't appear twice).
  hideBadge = false,
  ...rest
}) {
  const cap = useResourceCap(resourceKey);
  const [open, setOpen] = useState(false);

  const blocked = cap.atHardLimit;
  const upsellPath = `/plans?highlight=${encodeURIComponent(resourceKey)}`;

  const btn = (
    <Button
      {...rest}
      disabled={blocked || rest.disabled}
      className={className}
      data-testid={rest["data-testid"] || `cap-aware-${resourceKey}-btn`}
      data-cap-key={resourceKey}
    >
      {children}
    </Button>
  );

  return (
    <div className="inline-flex flex-col items-end gap-1">
      {blocked ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            {/*
              iter-170r — A11y: the wrapper announces itself as a button
              trigger so screen readers + touch users can engage it. On
              touch devices `onMouseEnter` never fires, so we also toggle
              on click (the underlying Button is disabled, so the click
              bubbles up to this span without firing the action).
            */}
            <span
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={
                isEs
                  ? `Llegaste al límite de ${featureLabel || "este recurso"}. Ver opciones de plan.`
                  : `You've hit your ${featureLabel || "resource"} limit. View plan options.`
              }
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              onClick={() => setOpen((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpen((v) => !v);
                }
              }}
              className="inline-flex outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded-md"
              data-testid={`cap-popover-trigger-${resourceKey}`}
            >
              {btn}
            </span>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="end"
            className="w-72 p-4 border-amber-300"
            data-testid={`cap-popover-${resourceKey}`}
          >
            <div className="flex items-start gap-2.5">
              <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-slate-900">
                  {isEs
                    ? `Llegaste al límite de ${featureLabel || "este recurso"}`
                    : `You've hit your ${featureLabel || "resource"} limit`}
                </div>
                <p className="mt-1 text-xs text-slate-600 leading-snug">
                  {isEs
                    ? `Usas ${cap.used}/${cap.limit}. Sube de plan para agregar más.`
                    : `Using ${cap.used}/${cap.limit}. Upgrade your plan to add more.`}
                </p>
                <Link
                  to={upsellPath}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                  data-testid={`cap-popover-cta-${resourceKey}`}
                >
                  {isEs ? "Ver planes" : "See plans"}
                  <ArrowUpRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        btn
      )}
      {!hideBadge && <CapUsageBadge resourceKey={resourceKey} isEs={isEs} showLabel />}
    </div>
  );
}
